/**
 * Hudl play-by-play CSV parser.
 *
 * Hudl lets teams customise column names and result values, so all matching
 * is case-insensitive and uses alias lists rather than exact column names.
 *
 * Flow:
 *   1. parseHudlCsv(text)          → HudlParseResult  (plays + unique players)
 *   2. aggregateHudlStats(result, idMap) → Map<playerId, Record<statKey, number>>
 */

// ── Public types ──────────────────────────────────────────────────────────────

export interface HudlPlay {
  playNumber: number;
  quarter: number | null;
  down: number | null;
  distance: number | null;
  yardLine: number | null;
  hash: string | null;
  odk: 'O' | 'D' | 'K' | null;
  personnel: string | null;
  formation: string | null;
  playType: string | null;
  result: string | null;
  yards: number | null;
  notes: string | null;
  passerRaw: string | null;
  receiverRaw: string | null;
  rusherRaw: string | null;
  tackler1Raw: string | null;
  tackler2Raw: string | null;
  returnerRaw: string | null;
  keyPlayerRaw: string | null;
}

export interface HudlPlayerRef {
  raw: string;        // original string from CSV
  jerseyNumber: string | null;
  name: string;       // best-guess name portion
}

export interface HudlParseResult {
  plays: HudlPlay[];
  /** Every unique non-empty player string found across all player columns. */
  uniquePlayers: HudlPlayerRef[];
  warnings: string[];
}

// ── Column alias resolution ───────────────────────────────────────────────────

const COL: Record<string, string[]> = {
  quarter:   ['quarter', 'qtr', 'q'],
  down:      ['down', 'dn'],
  distance:  ['distance', 'dist', 'yds to go', 'yards to go'],
  yardLine:  ['yardline', 'yard line', 'yd line', 'yard_line', 'yard ln', 'yd ln'],
  hash:      ['hash'],
  odk:       ['odk', 'o/d/k', 'side', 'offense/defense/kicking'],
  personnel: ['personnel', 'pers', 'offense personnel', 'off str', 'off_str'],
  formation: ['formation', 'form', 'offense formation', 'off form', 'off_form', 'offense form'],
  playType:  ['play type', 'play_type', 'type', 'play'],
  result:    ['result', 'play result', 'outcome'],
  yards:     ['gain/loss', 'gain / loss', 'gn/ls', 'yards', 'gain', 'yds', 'net yards'],
  notes:     ['play notes', 'notes', 'note', 'description'],
  passer:    ['passer', 'qb', 'thrower', 'passer name'],
  receiver:  ['receiver', 'rec', 'receiver name', 'target'],
  rusher:    ['rusher', 'runner', 'ball carrier', 'ballcarrier', 'rusher name'],
  tackler1:  ['tackler 1', 'tackler1', 'tackler', 'primary tackler', 'tackle 1', 'tackler_1'],
  tackler2:  ['tackler 2', 'tackler2', 'assist', 'secondary tackler', 'tackle 2', 'tackler_2'],
  returner:  ['returner', 'return man', 'returner name'],
  keyPlayer: ['key player', 'key_player', 'key player name', 'key'],
};

// ── Split-column player resolution (e.g. Hudl's PASSER_Jersey + PASSER_Name) ──

interface SplitPlayerIndices { jerseyIdx: number; nameIdx: number }

const SPLIT_PLAYER_PREFIXES: Record<string, string[]> = {
  passer:    ['passer'],
  receiver:  ['receiver'],
  rusher:    ['rusher'],
  tackler1:  ['tackler1', 'tackler 1'],
  tackler2:  ['tackler2', 'tackler 2'],
  returner:  ['returner'],
  keyPlayer: ['key player', 'key_player'],
};

function resolveSplitPlayerCols(headers: string[]): Record<string, SplitPlayerIndices> {
  const lower = headers.map((h) => h.trim().toLowerCase());
  const result: Record<string, SplitPlayerIndices> = {};
  for (const [role, prefixes] of Object.entries(SPLIT_PLAYER_PREFIXES)) {
    for (const prefix of prefixes) {
      const jerseyIdx = lower.findIndex((h) => h === `${prefix}_jersey` || h === `${prefix} jersey`);
      const nameIdx   = lower.findIndex((h) => h === `${prefix}_name`   || h === `${prefix} name`);
      if (jerseyIdx !== -1 && nameIdx !== -1) {
        result[role] = { jerseyIdx, nameIdx };
        break;
      }
    }
  }
  return result;
}

function resolveCol(headers: string[]): Record<string, number> {
  const lower = headers.map((h) => h.trim().toLowerCase());
  const idx: Record<string, number> = {};
  for (const [field, aliases] of Object.entries(COL)) {
    for (const alias of aliases) {
      const i = lower.indexOf(alias);
      if (i !== -1) { idx[field] = i; break; }
    }
  }
  return idx;
}

// ── CSV line parser (handles quoted fields) ───────────────────────────────────

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      i++;
      let field = '';
      while (i < line.length) {
        if (line[i] === '"' && line[i + 1] === '"') { field += '"'; i += 2; }
        else if (line[i] === '"') { i++; break; }
        else { field += line[i++]; }
      }
      result.push(field);
      if (line[i] === ',') i++;
    } else {
      let field = '';
      while (i < line.length && line[i] !== ',') field += line[i++];
      result.push(field.trim());
      if (line[i] === ',') i++;
    }
  }
  return result;
}

// ── Player string parser ──────────────────────────────────────────────────────

/** Extract jersey number and name from a Hudl player cell.
 *  Handles common formats:
 *    "12 Smith J"        → { jerseyNumber: '12', name: 'Smith J' }
 *    "#12 Smith J"       → { jerseyNumber: '12', name: 'Smith J' }
 *    "Smith, John (12)"  → { jerseyNumber: '12', name: 'Smith, John' }
 *    "Smith J #12"       → { jerseyNumber: '12', name: 'Smith J' }
 */
export function parsePlayerRef(raw: string): HudlPlayerRef {
  const s = raw.trim();
  if (!s) return { raw, jerseyNumber: null, name: s };

  // "Name (12)" or "Name (#12)"
  const parenMatch = s.match(/^(.+?)\s*\(#?(\d+)\)\s*$/);
  if (parenMatch) return { raw, jerseyNumber: parenMatch[2], name: parenMatch[1].trim() };

  // "Name #12"
  const hashSuffix = s.match(/^(.+?)\s+#(\d+)\s*$/);
  if (hashSuffix) return { raw, jerseyNumber: hashSuffix[2], name: hashSuffix[1].trim() };

  // "#12 Name" or "12 Name" or "12 - Name"
  const numFirst = s.match(/^#?(\d+)\s*[-–]?\s*(.+)$/);
  if (numFirst) return { raw, jerseyNumber: numFirst[1], name: numFirst[2].trim() };

  return { raw, jerseyNumber: null, name: s };
}

// ── ODK normaliser ────────────────────────────────────────────────────────────

function normaliseOdk(raw: string): 'O' | 'D' | 'K' | null {
  const v = raw.trim().toUpperCase();
  if (v === 'O' || v === 'OFFENSE' || v === 'OFF') return 'O';
  if (v === 'D' || v === 'DEFENSE' || v === 'DEF') return 'D';
  if (v === 'K' || v === 'KICKING' || v === 'KICK' || v === 'ST') return 'K';
  return null;
}

// ── Main parse function ───────────────────────────────────────────────────────

export function parseHudlCsv(text: string): HudlParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { plays: [], uniquePlayers: [], warnings: ['File appears to be empty or too short.'] };
  }

  // Find header row: first row where "result" or "odk" appears (case-insensitive)
  let headerIdx = 0;
  for (let i = 0; i < Math.min(lines.length, 5); i++) {
    const lower = lines[i].toLowerCase();
    if (lower.includes('result') || lower.includes('odk') || lower.includes('quarter')) {
      headerIdx = i;
      break;
    }
  }

  const headers = parseCsvLine(lines[headerIdx]);
  const colIdx = resolveCol(headers);
  const splitCols = resolveSplitPlayerCols(headers);
  const warnings: string[] = [];

  if (colIdx['result'] === undefined) {
    warnings.push('Could not find a "Result" column — stats may be incomplete.');
  }
  if (colIdx['odk'] === undefined) {
    warnings.push('Could not find an "ODK" column — all plays will be treated as offensive.');
  }

  const cell = (row: string[], field: string): string => {
    const i = colIdx[field];
    return i !== undefined ? (row[i] ?? '').trim() : '';
  };

  // For player fields: prefer a single combined column; fall back to split jersey+name columns.
  const cellPlayer = (row: string[], field: string): string => {
    const combined = cell(row, field);
    if (combined) return combined;
    const split = splitCols[field];
    if (!split) return '';
    const jersey = (row[split.jerseyIdx] ?? '').trim();
    const name   = (row[split.nameIdx]   ?? '').trim();
    if (jersey && name) return `${jersey} ${name}`;
    return jersey || name;
  };

  const plays: HudlPlay[] = [];
  const rawPlayerSet = new Set<string>();
  const playerRoles = ['passer', 'receiver', 'rusher', 'tackler1', 'tackler2', 'returner', 'keyPlayer'];

  for (let r = headerIdx + 1; r < lines.length; r++) {
    const row = parseCsvLine(lines[r]);
    if (row.every((c) => !c)) continue; // blank line

    const rawPasser    = cellPlayer(row, 'passer');
    const rawReceiver  = cellPlayer(row, 'receiver');
    const rawRusher    = cellPlayer(row, 'rusher');
    const rawTackler1  = cellPlayer(row, 'tackler1');
    const rawTackler2  = cellPlayer(row, 'tackler2');
    const rawReturner  = cellPlayer(row, 'returner');
    const rawKeyPlayer = cellPlayer(row, 'keyPlayer');

    for (const role of playerRoles) {
      const v = cellPlayer(row, role);
      if (v) rawPlayerSet.add(v);
    }

    const yardsRaw = cell(row, 'yards');
    const yards = yardsRaw !== '' ? parseInt(yardsRaw, 10) : null;

    const odkRaw = cell(row, 'odk');
    const odk = odkRaw ? normaliseOdk(odkRaw) : null;

    plays.push({
      playNumber: r - headerIdx,
      quarter:    parseInt(cell(row, 'quarter'), 10)  || null,
      down:       parseInt(cell(row, 'down'),    10)  || null,
      distance:   parseInt(cell(row, 'distance'),10)  || null,
      yardLine:   parseInt(cell(row, 'yardLine'),10)  || null,
      hash:       cell(row, 'hash')      || null,
      odk,
      personnel:  cell(row, 'personnel') || null,
      formation:  cell(row, 'formation') || null,
      playType:   cell(row, 'playType')  || null,
      result:     cell(row, 'result')    || null,
      yards:      Number.isFinite(yards) ? yards : null,
      notes:      cell(row, 'notes')     || null,
      passerRaw:    rawPasser    || null,
      receiverRaw:  rawReceiver  || null,
      rusherRaw:    rawRusher    || null,
      tackler1Raw:  rawTackler1  || null,
      tackler2Raw:  rawTackler2  || null,
      returnerRaw:  rawReturner  || null,
      keyPlayerRaw: rawKeyPlayer || null,
    });
  }

  const uniquePlayers = Array.from(rawPlayerSet).map(parsePlayerRef);
  return { plays, uniquePlayers, warnings };
}

// ── Result keyword helpers ────────────────────────────────────────────────────

const rl = (r: string | null) => (r ?? '').toLowerCase();

const isTD          = (r: string | null) => /\btd\b|touchdown/.test(rl(r));
const isComplete    = (r: string | null) => rl(r).includes('complet') && !rl(r).includes('incomplet');
const isIncomplete  = (r: string | null) => rl(r).includes('incomplet') || rl(r).includes('incomplete');
const isSack        = (r: string | null) => rl(r).includes('sack');
const isInt         = (r: string | null) => /\bint\b|interception/.test(rl(r));
const isFumble      = (r: string | null) => rl(r).includes('fumble');
const isFumbleRec   = (r: string | null) => /fumble rec|fumble recovery/.test(rl(r));
const isForcedFumble= (r: string | null) => /forced fumble|force fumble|ff\b/.test(rl(r));
const isFGGood      = (r: string | null) => /(field goal|fg).*(good|made|success)|good.*field goal/.test(rl(r));
const isXPGood      = (r: string | null) => /(extra point|pat\b|xp\b|point after).*(good|made|success)/.test(rl(r));
const isTwoPtGood   = (r: string | null) => /2[- ]?pt|two[- ]?point|2[- ]?point/.test(rl(r)) && /(good|made|success|convert)/.test(rl(r));
const isSafety      = (r: string | null) => /\bsafety\b/.test(rl(r));
const isPassDef     = (r: string | null) => /pass\s*(def|break|break.?up|deflect)|pd\b|pbu\b/.test(rl(r));
const isTFL         = (r: string | null) => /tackle.*loss|tfl\b/.test(rl(r));

function longPlayBucket(yards: number | null): ('20' | '30' | '40')[] {
  if (!yards || yards <= 0) return [];
  const buckets: ('20' | '30' | '40')[] = [];
  if (yards >= 20) buckets.push('20');
  if (yards >= 30) buckets.push('30');
  if (yards >= 40) buckets.push('40');
  return buckets;
}

// ── Score derivation ─────────────────────────────────────────────────────────

/** Derive both teams' scores from play results.
 *  O plays  → our scoring (TD/FG/XP/2pt) or opponent safety
 *  D plays  → opponent scoring (TD/FG/XP/2pt) or our safety
 *  K plays  → our FG/XP/return-TD
 */
export function deriveScoreFromPlays(plays: HudlPlay[]): { ourScore: number; opponentScore: number } {
  let ours = 0;
  let theirs = 0;
  for (const { odk, result } of plays) {
    if (odk === 'O' || odk === null) {
      if (isTD(result))       ours   += 6;
      if (isFGGood(result))   ours   += 3;
      if (isXPGood(result))   ours   += 1;
      if (isTwoPtGood(result)) ours  += 2;
      if (isSafety(result))   theirs += 2; // our offense tackled in own end zone
    } else if (odk === 'D') {
      if (isTD(result))       theirs += 6;
      if (isFGGood(result))   theirs += 3;
      if (isXPGood(result))   theirs += 1;
      if (isTwoPtGood(result)) theirs += 2;
      if (isSafety(result))   ours   += 2; // our defense tackled them in their end zone
    } else if (odk === 'K') {
      if (isFGGood(result))   ours += 3;
      else if (isXPGood(result)) ours += 1;
      else if (isTD(result))  ours += 6; // our return TD
    }
  }
  return { ourScore: ours, opponentScore: theirs };
}

// ── Stat aggregation ──────────────────────────────────────────────────────────

/** idMap: raw player string → player UUID (built from user's match selections) */
export function aggregateHudlStats(
  plays: HudlPlay[],
  idMap: Map<string, string>,
): Map<string, Record<string, number>> {
  const stats = new Map<string, Record<string, number>>();

  const add = (playerId: string | undefined, key: string, val = 1) => {
    if (!playerId) return;
    if (!stats.has(playerId)) stats.set(playerId, {});
    const m = stats.get(playerId)!;
    m[key] = (m[key] ?? 0) + val;
  };

  const pid = (raw: string | null) => (raw ? idMap.get(raw) : undefined);

  for (const play of plays) {
    const { odk, result, yards } = play;

    const passerId   = pid(play.passerRaw);
    const receiverId = pid(play.receiverRaw);
    const rusherId   = pid(play.rusherRaw);
    const tackler1Id = pid(play.tackler1Raw);
    const tackler2Id = pid(play.tackler2Raw);
    const returnerId = pid(play.returnerRaw);
    const keyId      = pid(play.keyPlayerRaw);

    // ── Offensive plays ───────────────────────────────────────────────────────
    if (odk === 'O' || odk === null) {
      const hasPass = !!play.passerRaw;
      const hasRush = !!play.rusherRaw && !hasPass;

      if (hasPass) {
        // PassAtt for every pass play (complete, incomplete, sack, int)
        if (!isSack(result)) add(passerId, 'PassAtt');

        if (isComplete(result) || isTD(result)) {
          add(passerId, 'PassComp');
          if (yards != null && yards > 0) {
            add(passerId,   'PassYds', yards);
            add(receiverId, 'RecYds',  yards);
            for (const b of longPlayBucket(yards)) {
              add(passerId,   `Pass${b}Plus`);
              add(receiverId, `Rec${b}Plus`);
            }
          }
          add(receiverId, 'Rec');
          add(receiverId, 'Targets');

          if (isTD(result)) {
            add(passerId,   'PassTD');
            add(receiverId, 'RecTD');
          }
        } else if (isIncomplete(result)) {
          add(receiverId, 'Targets');
        } else if (isInt(result)) {
          add(passerId, 'PassInt');
        }
      }

      if (hasRush || (!hasPass && !!play.rusherRaw)) {
        add(rusherId, 'RushAtt');
        if (yards != null) {
          if (yards > 0) {
            add(rusherId, 'RushYds', yards);
            for (const b of longPlayBucket(yards)) add(rusherId, `Rush${b}Plus`);
          }
        }
        if (isTD(result)) add(rusherId, 'RushTD');
        if (isFumble(result)) add(rusherId, 'RushFumble');
      }
    }

    // ── Defensive plays ───────────────────────────────────────────────────────
    if (odk === 'D') {
      // Tackles credited to TACKLER1 and TACKLER2
      if (tackler1Id) {
        add(tackler1Id, 'Tackles');
        if (isSack(result))      add(tackler1Id, 'Sacks');
        if (isTFL(result))       add(tackler1Id, 'TFL');
        if (isFumbleRec(result)) add(tackler1Id, 'FumbleRec');
      }
      if (tackler2Id) {
        add(tackler2Id, 'Tackles');
        if (isSack(result))      add(tackler2Id, 'Sacks');
        if (isTFL(result))       add(tackler2Id, 'TFL');
      }
      // Key player gets INT, FF, PassDef, FumbleRec
      const defId = keyId ?? tackler1Id;
      if (isInt(result))          add(defId, 'Int');
      if (isForcedFumble(result)) add(defId, 'FF');
      if (isPassDef(result))      add(defId, 'PassDef');
      if (isFumbleRec(result) && !tackler1Id) add(defId, 'FumbleRec');
    }

    // ── Special teams ─────────────────────────────────────────────────────────
    if (odk === 'K') {
      if (isFGGood(result)) {
        const kickerId = keyId ?? tackler1Id;
        add(kickerId, 'FG');
      }
      if (isXPGood(result)) {
        const kickerId = keyId ?? tackler1Id;
        add(kickerId, 'XP');
      }
      if (isTD(result) && returnerId) add(returnerId, 'TD');
    }
  }

  return stats;
}
